/**
 * WonderLabs desktop density policy.
 *
 * Screen CSS dimensions are stable across ordinary Ctrl+/Ctrl- browser zoom
 * in desktop Chrome/Edge, unlike innerWidth / useWindowDimensions / DPR.
 * We sample ONCE per mounting: accessibility zoom must not trigger a new,
 * compensating shrink. The compact profile is a display-density heuristic,
 * not a claim that browsers expose the Windows scaling preference directly.
 * Mobile layouts are already responsive and are never miniaturized.
 */
export function chooseWonderLabsDensity({ screenWidth, screenHeight, explicitAppZoom = false }) {
  const w = Number(screenWidth);
  const h = Number(screenHeight);
  if (explicitAppZoom || !Number.isFinite(w) || !Number.isFinite(h) || w < 900 || h < 600) return 1;
  if (w <= 1366 && h <= 820) return 0.70;
  if (w <= 1600 && h <= 960) return 0.84;
  return 1;
}

export function getWonderLabsDisplayDensity() {
  if (typeof window === "undefined" || typeof document === "undefined") return 1;
  try {
    // Saras-3D already has a user-controlled body zoom. Do not fight that.
    const explicitAppZoom = window.localStorage?.getItem("app_zoom_user_set") === "true";
    return chooseWonderLabsDensity({
      screenWidth: window.screen?.width,
      screenHeight: window.screen?.height,
      explicitAppZoom,
    });
  } catch (_error) {
    return 1;
  }
}

/** Apply scoped WonderLabs density without changing the viewport footprint.
 *
 * Chromium evaluates percentage widths/heights in the zoomed element's
 * containing-block coordinate system. Setting both zoom: .7 AND
 * width/height: 142.857% actually produces a 142.857%-of-viewport box; the
 * activity footer and the Swan close button fall BELOW/RIGHT of the viewport.
 * A 100% width/height already fills the containing block at CSS zoom .7.
 *
 * Keep the sampled density stable across the user's browser zoom changes;
 * regular reflow inside this 100%-sized surface handles accessibility zoom.
 */
export function configureWonderLabsSurface(node, density) {
  if (!node || !node.style) return () => {};
  const scale = density > 0 && density <= 1 ? density : 1;
  const properties = ["zoom", "width", "height", "flex", "maxWidth", "maxHeight"];
  const previous = Object.fromEntries(properties.map((name) => [name, node.style[name]]));
  node.style.zoom = String(scale);
  node.style.width = "100%";
  node.style.height = "100%";
  node.style.flex = "none";
  node.style.maxWidth = "none";
  node.style.maxHeight = "none";
  return () => { for (const [name, value] of Object.entries(previous)) node.style[name] = value; };
}
